import { Component, OnInit } from "@angular/core";
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";
import { ErrorHandlerDialogComponent } from "../../error-handler-dialog/error-handler-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ExcelExportService } from "../../../services/excel-export.service";
import { VehicleRolloutService } from "../../../services/vehicle-rollout.service";
import { ReportConstants } from "../ReportConstants";
import { ReaderService } from "../../../services/reader.service";
import * as _ from "lodash";

interface Menu {
  model: UntypedFormControl;
  availableOptions: any[];
  selectedOptions: any[];
}

@Component({
    selector: "app-vehicle-rollout",
    templateUrl: "./vehicle-rollout.component.html",
    styleUrls: ["./vehicle-rollout.component.scss"],
    standalone: false
})
export class VehicleRolloutComponent implements OnInit {
  vehicleRolloutList: any = [];
  columnList = [];
  isLoading = false;
  displayTable = false;
  constants = new ReportConstants();

  defaultBrand: any = [];

  year = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: "",
  };

  region: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  oem: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  brand: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  bodyStyle: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  segment: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  formGroup = new FormGroup({
    year: this.year.model,
    region: this.region.model,
    oem: this.oem.model,
    brand: this.brand.model,
    bodyStyle: this.bodyStyle.model,
    segment: this.segment.model,
  });

  constructor(
    public dialog: MatDialog,
    private readonly vehicleRolloutService: VehicleRolloutService,
    private readonly excelExport: ExcelExportService,
    private readonly readerService: ReaderService
  ) {}

  ngOnInit(): void {
    this.beginLoading();
    this.vehicleRolloutService.getVehicleReportRolloutMenuOptions().subscribe({
      next: (x: any) => {
        this.handleVehicleRolloutMenuItems(x);
      },
      error: (err: any) => {
        this.endLoading();
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: err.error == null ? err.message : err.error.message,
          },
        });
      },
      complete: () => {
        this.subcribeToFormControls();
        this.selectAllMenuOptions();
        this.formGroup.updateValueAndValidity();
        this.endLoading();
      },
    });
  }

  public subcribeToFormControls(): void {
    this.formGroup.controls["year"].valueChanges.subscribe((value) => {
      this.onChangeYear(value);
    });
    this.formGroup.controls["region"].valueChanges.subscribe((value) => {
      this.onChangeRegion(value);
    });
    this.formGroup.controls["oem"].valueChanges.subscribe((value) => {
      this.onChangeOem(value);
    });
    this.formGroup.controls["brand"].valueChanges.subscribe((value) => {
      this.onChangeBrand(value);
    });
    this.formGroup.controls["bodyStyle"].valueChanges.subscribe((value) => {
      this.onChangeBodyStyle(value);
    });
    this.formGroup.controls["segment"].valueChanges.subscribe((value) => {
      this.onChangeSegment(value);
    });
  }

  public generateReport(): void {
    if (this.verifySearchCriteria()) {
      this.displayTable = false;
      this.beginLoading();
      this.vehicleRolloutService
        .getVehicleReportRolloutData(
          this.year.selectedOptions,
          this.oem.selectedOptions,
          this.brand.selectedOptions,
          this.segment.selectedOptions,
          this.bodyStyle.selectedOptions,
          this.region.selectedOptions
        )
        .subscribe({
          next: (x: any) => {
            this.handleVehicleRolloutReportData(x);
          },
          error: (err: any) => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: {
                message: err.error == null ? err.message : err.error.message,
              },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    } else {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: { message: "Please specify search criteria." },
      });
    }
  }

  public handleVehicleRolloutReportData(data: {
    displayDataList: string | any[];
    columnNames: any[];
  }): void {
    if (data.displayDataList && data.displayDataList.length > 0) {
      this.displayTable = true;
      this.vehicleRolloutList = data.displayDataList;
      this.columnList = data.columnNames;
    } else {
      this.clearReport();
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: {
          message:
            "There are no entries matching the selected report criteria.",
        },
      });
    }
  }

  public optionsToString(options: string[]): string {
    let ret: string;
    ret = options[0] + ",";
    if (options.length > 1) {
      for (let i = 1; i < options.length; i++) {
        ret += "," + options[i];
      }
    }
    return ret;
  }

  public beginLoading(): void {
    this.isLoading = true;
  }

  public endLoading(): void {
    this.isLoading = false;
  }

  public onChangeYear(newValue: string): void {
    this.year.selectedOptions = newValue;
  }

  public onChangeRegion(newValue: string[]): void {
    this.region.selectedOptions = newValue;
  }

  public onChangeOem(newValue: string[]): void {
    this.oem.selectedOptions = newValue;
    if (newValue != null && newValue.length > 0) {
      const oems = this.optionsToString(newValue);
      this.readerService.getBrandForOem(oems).subscribe({
        next: (res) => {
          this.brand.selectedOptions = _.filter(
            this.brand.selectedOptions,
            (x) => {
              return res.includes(x);
            }
          );
          this.formGroup.controls["brand"].setValue(
            this.brand.selectedOptions,
            { onlySelf: true, emitEvent: false }
          );
          this.brand.availableOptions = res;
        },
        error: (res) => {
          console.log(res);
        },
        complete: () => {},
      });
    }
  }

  public onChangeBrand(newValue: string[]): void {
    this.brand.selectedOptions = newValue;
  }

  public onChangeBodyStyle(newValue: string[]): void {
    this.bodyStyle.selectedOptions = newValue;
  }

  public onChangeSegment(newValue: string[]): void {
    this.segment.selectedOptions = newValue;
  }

  public exportToExcel(): void {
    if (!this.verifySearchCriteria()) {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: {
          message: "Please specify search criteria.",
        },
      });
    } else {
      this.beginLoading();
      this.vehicleRolloutService
        .exportToExcel(
          this.year.selectedOptions,
          this.oem.selectedOptions,
          this.brand.selectedOptions,
          this.segment.selectedOptions,
          this.bodyStyle.selectedOptions,
          this.region.selectedOptions
        )
        .subscribe({
          next: (response: any) => {
            this.excelExport.exportToExcelFromServer(
              response,
              "VehicleRolloutReport"
            );
          },
          error: () => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: { message: "Error occurred while creating excel file." },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    }
  }

  public clearFilters(): void {
    this.year.selectedOptions = "";
    this.formGroup.controls["year"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });
    this.formGroup.controls["year"].markAsUntouched();

    this.region.selectedOptions = [];
    this.formGroup.controls["region"].setValue([], {
      onlySelf: true,
      emitEvent: false,
    });

    this.oem.selectedOptions = [];
    this.formGroup.controls["oem"].setValue([], {
      onlySelf: true,
      emitEvent: false,
    });

    this.brand.selectedOptions = [];
    this.formGroup.controls["brand"].setValue([], {
      onlySelf: true,
      emitEvent: false,
    });

    this.bodyStyle.selectedOptions = [];
    this.formGroup.controls["bodyStyle"].setValue([], {
      onlySelf: true,
      emitEvent: false,
    });

    this.segment.selectedOptions = [];
    this.formGroup.controls["segment"].setValue([], {
      onlySelf: true,
      emitEvent: false,
    });

    this.formGroup.markAsUntouched();
    this.formGroup.reset();
    this.brand.availableOptions = this.defaultBrand;
  }

  public handleVehicleRolloutMenuItems(menuItems: any): void {
    this.year.availableOptions = menuItems.yearsItems;
    this.region.availableOptions = menuItems.regionItems;
    this.oem.availableOptions = menuItems.oemItems;
    this.brand.availableOptions = menuItems.brandItems;
    this.defaultBrand = menuItems.brandItems;
    this.bodyStyle.availableOptions = menuItems.bodyStyleItems;
    this.segment.availableOptions = menuItems.segmentItems;
  }

  public verifySearchCriteria(): boolean {
    return this.formGroup.valid;
  }

  public clearReport(): void {
    this.displayTable = false;
    this.vehicleRolloutList = [];
    this.columnList = [];
  }

  public selectAllMenuOptions(): void {
    const date = new Date();
    this.year.selectedOptions = String(date.getFullYear());
    this.formGroup.controls["year"].setValue(this.year.selectedOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.region.selectedOptions = this.region.availableOptions;
    this.formGroup.controls["region"].setValue(this.region.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.oem.selectedOptions = this.oem.availableOptions;
    this.formGroup.controls["oem"].setValue(this.oem.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.brand.availableOptions = this.defaultBrand;
    this.brand.selectedOptions = this.brand.availableOptions;
    this.formGroup.controls["brand"].setValue(this.brand.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.segment.selectedOptions = this.segment.availableOptions;
    this.formGroup.controls["segment"].setValue(this.segment.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.bodyStyle.selectedOptions = this.bodyStyle.availableOptions;
    this.formGroup.controls["bodyStyle"].setValue(
      this.bodyStyle.availableOptions,
      { onlySelf: true, emitEvent: false }
    );
  }

  public centerText(column: any): boolean {
    let result = false;
    if (!isNaN(column)) {
      result = true;
    }
    return result;
  }
}
